import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, RefreshCw, GripVertical, AlertTriangle, Tag } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * AdminCategoriesPanel — full CRUD for storefront categories.
 *
 * Storefront nav + product filtering automatically reflect changes since
 * both read from GET /api/categories. Slugs are immutable after creation
 * so product docs never desync — renames update `name` only.
 *
 * Deletion is blocked backend-side while any product still references the
 * category; the UI surfaces the reason via a red banner on the row.
 */
export const AdminCategoriesPanel = ({ token }) => {
    const headers = { "X-Admin-Token": token };
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(null);
    const [creating, setCreating] = useState(false);
    const [newCat, setNewCat] = useState({ slug: "", name: "", description: "", accent: "#C89B4A" });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/categories", { headers });
            setItems(data.map((c) => ({ ...c, _dirty: false })));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load categories");
        }
        setLoading(false);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const update = (slug, key, value) => {
        setItems((p) => p.map((c) => (c.slug === slug ? { ...c, [key]: value, _dirty: true } : c)));
    };

    const save = async (cat) => {
        setSaving(cat.slug);
        try {
            const { data } = await api.patch(
                `/admin/categories/${cat.slug}`,
                { name: cat.name, description: cat.description, accent: cat.accent },
                { headers }
            );
            setItems((p) => p.map((c) => (c.slug === cat.slug ? { ...data, _dirty: false } : c)));
            toast.success(`Saved “${data.name}”`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
        setSaving(null);
    };

    const del = async (slug) => {
        if (!window.confirm(`Delete this category?`)) return;
        try {
            await api.delete(`/admin/categories/${slug}`, { headers });
            setItems((p) => p.filter((c) => c.slug !== slug));
            toast.success("Category deleted");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const move = async (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= items.length) return;
        const next = [...items];
        [next[idx], next[target]] = [next[target], next[idx]];
        setItems(next);
        try {
            await api.post("/admin/categories/reorder", { slugs: next.map((c) => c.slug) }, { headers });
        } catch {
            toast.error("Reorder failed");
            load();
        }
    };

    const submitNew = async (e) => {
        e.preventDefault();
        if (!newCat.slug || !newCat.name) return;
        setCreating(true);
        try {
            const { data } = await api.post("/admin/categories", newCat, { headers });
            setItems((p) => [...p, { ...data, _dirty: false }]);
            setNewCat({ slug: "", name: "", description: "", accent: "#C89B4A" });
            toast.success(`Created “${data.name}”`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Create failed");
        }
        setCreating(false);
    };

    const slugFromName = (n) =>
        n.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

    if (loading) {
        return <div className="py-12 text-center text-[var(--text-secondary)]">Loading categories…</div>;
    }

    return (
        <div className="space-y-6" data-testid="admin-categories-panel">
            <div className="flex items-center justify-between">
                <h3 className="font-display text-xl font-bold text-white inline-flex items-center gap-2">
                    <Tag size={18} className="text-[var(--gold)]" /> Product Categories
                </h3>
                <button onClick={load} className="btn-neon" data-testid="categories-refresh">
                    <RefreshCw size={14} /> Reload
                </button>
            </div>

            {/* Create form */}
            <form
                onSubmit={submitNew}
                className="glass-elevated rounded-2xl p-5 space-y-3"
                data-testid="category-create-form"
            >
                <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--gold)]">// Create new category</div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <input
                        type="text"
                        placeholder="Name (e.g. Sleep & Recovery)"
                        value={newCat.name}
                        onChange={(e) => setNewCat({ ...newCat, name: e.target.value, slug: newCat.slug || slugFromName(e.target.value) })}
                        className="px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                        data-testid="new-category-name"
                        required
                    />
                    <input
                        type="text"
                        placeholder="Slug (kebab-case)"
                        value={newCat.slug}
                        onChange={(e) => setNewCat({ ...newCat, slug: e.target.value.toLowerCase() })}
                        className="px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)] font-mono"
                        data-testid="new-category-slug"
                        pattern="^[a-z0-9]+(-[a-z0-9]+)*$"
                        required
                    />
                    <input
                        type="text"
                        placeholder="Short description"
                        value={newCat.description}
                        onChange={(e) => setNewCat({ ...newCat, description: e.target.value })}
                        className="md:col-span-2 px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                        data-testid="new-category-description"
                    />
                </div>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <label className="inline-flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                        Accent
                        <input
                            type="text"
                            value={newCat.accent}
                            onChange={(e) => setNewCat({ ...newCat, accent: e.target.value })}
                            className="px-3 py-1.5 bg-black/40 border border-white/10 rounded-full text-xs text-white w-40 font-mono"
                            data-testid="new-category-accent"
                        />
                    </label>
                    <button type="submit" disabled={creating} className="btn-solid" data-testid="category-create-submit">
                        <Plus size={14} /> {creating ? "Creating…" : "Create category"}
                    </button>
                </div>
            </form>

            {/* Existing categories */}
            <div className="space-y-3">
                {items.map((cat, idx) => (
                    <div
                        key={cat.slug}
                        className="glass rounded-2xl p-4 md:p-5"
                        data-testid={`category-row-${cat.slug}`}
                    >
                        <div className="flex items-start gap-3">
                            <div className="flex flex-col gap-1 pt-1">
                                <button
                                    onClick={() => move(idx, -1)}
                                    disabled={idx === 0}
                                    className="w-6 h-6 grid place-items-center rounded text-[var(--text-muted)] hover:text-[var(--gold)] disabled:opacity-30"
                                    aria-label="Move up"
                                    data-testid={`category-move-up-${cat.slug}`}
                                >▲</button>
                                <GripVertical size={14} className="text-[var(--text-muted)] mx-auto" />
                                <button
                                    onClick={() => move(idx, 1)}
                                    disabled={idx === items.length - 1}
                                    className="w-6 h-6 grid place-items-center rounded text-[var(--text-muted)] hover:text-[var(--gold)] disabled:opacity-30"
                                    aria-label="Move down"
                                    data-testid={`category-move-down-${cat.slug}`}
                                >▼</button>
                            </div>

                            <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-2">
                                <div>
                                    <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] block mb-1">Slug</label>
                                    <div className="px-3 py-1.5 bg-black/30 border border-white/5 rounded text-xs text-[var(--text-secondary)] font-mono">
                                        {cat.slug}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] block mb-1">Name</label>
                                    <input
                                        type="text"
                                        value={cat.name || ""}
                                        onChange={(e) => update(cat.slug, "name", e.target.value)}
                                        className="w-full px-3 py-1.5 bg-black/40 border border-white/10 rounded text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                                        data-testid={`category-name-${cat.slug}`}
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] block mb-1">Description</label>
                                    <input
                                        type="text"
                                        value={cat.description || ""}
                                        onChange={(e) => update(cat.slug, "description", e.target.value)}
                                        className="w-full px-3 py-1.5 bg-black/40 border border-white/10 rounded text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                                        data-testid={`category-description-${cat.slug}`}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-between gap-3 mt-3 flex-wrap pl-9">
                            <div className="flex items-center gap-3 text-xs text-[var(--text-secondary)]">
                                <span className="inline-flex items-center gap-1.5 text-[var(--gold)]">
                                    <span
                                        className="inline-block w-2.5 h-2.5 rounded-full"
                                        style={{ background: cat.accent || "#C89B4A" }}
                                    />
                                    {cat.product_count} product{cat.product_count === 1 ? "" : "s"}
                                </span>
                                {cat._dirty && (
                                    <span className="text-[10px] font-mono uppercase tracking-widest text-amber-400">
                                        · Unsaved
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => save(cat)}
                                    disabled={saving === cat.slug || !cat._dirty}
                                    className="btn-neon !py-1.5 !px-3 !text-[10px] disabled:opacity-40"
                                    data-testid={`category-save-${cat.slug}`}
                                >
                                    <Save size={12} /> {saving === cat.slug ? "Saving…" : "Save"}
                                </button>
                                <button
                                    onClick={() => del(cat.slug)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-widest border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors"
                                    data-testid={`category-delete-${cat.slug}`}
                                    disabled={cat.product_count > 0}
                                    title={cat.product_count > 0 ? "Reassign products first" : "Delete"}
                                >
                                    <Trash2 size={12} /> Delete
                                </button>
                            </div>
                        </div>

                        {cat.product_count > 0 && (
                            <div className="mt-3 pl-9 flex items-start gap-2 text-[11px] text-amber-300/80 bg-amber-500/5 border border-amber-500/20 rounded-lg p-2">
                                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                                Deletion is blocked while {cat.product_count} product{cat.product_count === 1 ? " is" : "s are"} still assigned to this category. Reassign them first.
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AdminCategoriesPanel;
